import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useWebsocketSubscription } from 'hooks/useWebsocketSubscription';
import { WorkflowStatus } from 'models/Workflow';
import { portalConnectionQueryKeys } from 'services/api/portalConnections';
import { indexWorkflows, WorkflowListParams, workflowQueryKeys } from 'services/api/workflows';

import { PORTAL_AUTOMATION_API_NAME } from '../constants';

const NUDGE_DEBOUNCE_MS = 400;
const NUDGE_MAX_WAIT_MS = 1500;

// A backstop for a dropped socket, not a refresh cadence — the nudge is how this list stays
// live. Deliberately long: the whole page is a projection over `action_required`, so a poll
// that fires while nothing settled re-fetches a list that cannot have changed.
const FALLBACK_POLL_INTERVAL_MS = 300_000;

/**
 * The attention rule, expressed as a query: `portal_automation` runs in `action_required`,
 * and nothing else. This list is not a run monitor — a run in flight requires nobody's
 * attention, so it is not here, which is why no "running vs. waiting on you" indicator exists.
 *
 * Filtered SERVER-side (the index takes both parameters) rather than fetched whole and sieved:
 * the unfiltered index is lifetime-scoped and terminal-inclusive, so a client with a year of
 * referrals would ship megabytes to render a handful of cards.
 */
const TASK_LIST_PARAMS: WorkflowListParams = {
  definitionApiName: [PORTAL_AUTOMATION_API_NAME],
  status: [WorkflowStatus.ACTION_REQUIRED],
};

export default function useTasks() {
  const queryClient = useQueryClient();
  const [subscriptionRejected, setSubscriptionRejected] = useState(false);
  const [everConnected, setEverConnected] = useState(false);

  const query = useQuery({
    queryKey: workflowQueryKeys.list(TASK_LIST_PARAMS),
    queryFn: ({ signal }) => indexWorkflows(TASK_LIST_PARAMS, signal).then((page) => page.data),
    refetchInterval: FALLBACK_POLL_INTERVAL_MS,
  });

  // One subscription for the page — never one per card. WorkflowChannel does a policy-scoped
  // lookup per subscription, so a page of thirty cards would cost thirty DB reads on load for
  // information this client-wide nudge already carries.
  // Both projections the page draws from, not just the cards. The banner and the operational
  // cards read connections, so leaving that query out left a sweep that started elsewhere
  // unannounced and a finished one banner-ed until remount — `refetchOnWindowFocus` is off
  // globally, so nothing else would have refreshed it.
  const nudged = useMemo(
    () =>
      debounce(
        () => {
          queryClient.invalidateQueries({ queryKey: workflowQueryKeys.lists });
          queryClient.invalidateQueries({ queryKey: portalConnectionQueryKeys.base });
        },
        NUDGE_DEBOUNCE_MS,
        { maxWait: NUDGE_MAX_WAIT_MS }
      ),
    [queryClient]
  );
  useEffect(() => () => nudged.cancel(), [nudged]);

  // The nudge is contentless and client-wide: any run settling anywhere wakes this page. The
  // filtered fetch is what keeps that affordable.
  const { isConnected } = useWebsocketSubscription({
    channel: 'WorkflowListChannel',
    onReceived: () => nudged(),
    // A rejected subscription is otherwise silent, and a silently dead socket looks exactly
    // like a page with no work on it. Surfacing it lets the page say it may be stale.
    onRejected: useCallback(() => setSubscriptionRejected(true), []),
  });

  useEffect(() => {
    if (isConnected) setEverConnected(true);
  }, [isConnected]);

  return {
    workflows: query.data ?? [],
    isLoading: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
    // Live means the nudge is arriving. False falls back to the slow poll, which is worth
    // telling the reader about — this surface promises cards that appear and vanish on their own.
    isLive: isConnected && !subscriptionRejected,
    // What the page actually renders on. `isConnected` is false while the socket is still
    // opening, so "not live" on first paint means "connecting", not "stale" — saying so then
    // would put a warning on every single load. A rejection is known bad immediately; a drop
    // is only knowable once there was something to drop.
    isStale: subscriptionRejected || (everConnected && !isConnected),
  };
}
